import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { baseApiClient } from 'api/BaseApiClient';

import { useUpdateProperties } from 'hooks/properties';
import { useAuth } from 'hooks/useAuth';
import { getEnv } from 'hooks/useCurrentEnv';

type SandboxContextType = {
  isSandboxOn: boolean;
  tooltipMessage: string;
  onSandboxSwitch: (isSandboxSwitchOn: boolean) => boolean;
};

const defaultContextValue: SandboxContextType = {
  isSandboxOn: true,
  tooltipMessage: '',
  onSandboxSwitch: () => true,
};

export const SandboxContext = createContext(defaultContextValue);

const SandboxContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSandbox, setIsSandbox] = useState<boolean>(defaultContextValue.isSandboxOn);
  const [tooltipMessage, setTooltipMessage] = useState(defaultContextValue.tooltipMessage);
  const { updateProperties } = useUpdateProperties();

  const setSandbox = useCallback(
    (isSandboxOn: boolean) => {
      const env = getEnv(isSandboxOn, user?.profile);

      baseApiClient.setEnv(env);
      setIsSandbox(isSandboxOn);
    },
    [user],
  );

  const isOrderManager =
    user?.profile?.role?.id === 'orders_manager' || user?.profile?.role?.id === 'orders_tasks_manager';

  useEffect(() => {
    if (user) {
      if ((user.hasProductionKey || user.hasDevelopmentKey) && (isOrderManager || !user.properties?.isSandboxMode)) {
        setSandbox(false);
      } else {
        setSandbox(true);
      }
    }
  }, [user, isOrderManager]);

  const handleSandboxSwitch = (isSandboxSwitchOn: boolean) => {
    setTooltipMessage('');

    if (isOrderManager) {
      const orderManagerTooltip =
        user?.hasProductionKey || user?.hasDevelopmentKey
          ? 'You can only use production mode once the Production API keys are issued'
          : 'Production mode will be enabled once the Production API keys are issued';

      setTooltipMessage(orderManagerTooltip);

      return false;
    }

    if (user?.hasProductionKey || user?.hasDevelopmentKey) {
      setSandbox(isSandboxSwitchOn);
      updateProperties(
        { isSandboxMode: isSandboxSwitchOn },
        {
          onError(error) {
            // eslint-disable-next-line no-console
            console.error('failed to save sandbox state', error);
          },
          onSuccess() {
            queryClient.invalidateQueries(['getWidgetSettings']);
          },
        },
      );

      return true;
    }

    return false;
  };

  return (
    <SandboxContext.Provider
      value={{
        isSandboxOn: isSandbox,
        tooltipMessage,
        onSandboxSwitch: handleSandboxSwitch,
      }}
    >
      {children}
    </SandboxContext.Provider>
  );
};

function useSandboxContext() {
  const context = useContext(SandboxContext);

  if (context === undefined) {
    throw new Error('useSandboxContext must be used within a SandboxContextProvider');
  }

  return context;
}

export { SandboxContextProvider, useSandboxContext };
